import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'
import { GET_USER_FILES, SAVE_USER_FILE, UPLOAD_FILE } from 'store/types'
import CommonApi from 'api/common'
import { getUserFilesAction, saveUserFileAction, uploadFileAction } from 'store/actions/common'
import { get } from 'lodash'
import i18n from 'i18n'
import { getFileData } from 'utils/helper'
import { showErrorToast, showSuccessToast } from 'utils/toastHelpers'
import { CustomFile } from 'types'

const UNKNOWN_FILE_TYPE = 'application/octet-stream'
// The stored copy is named after the file's extension. Without a plain one (no extension, or
// "v1.2 final") that name would hold spaces and its link would not open.
const nameForLink = (name: string) => (/\.[a-z0-9]+$/i.test(name) ? name : `${name}.bin`)

export function* uploadUserFile(
  files: CustomFile[] | FileList,
  isMultiple?: boolean,
  ownerId?: string
) {
  const tenantId = yield select((state) => state.user.tenantId)
  // Whose files these are is settled when the upload starts: by the time the files are up, an
  // advisor may have opened another client. The Files panel says whose they are (ownerId); the
  // client last opened may be someone else's.
  const clientId = yield select((state) => state.clients?.client?.profile?.id)
  const userId = yield select((state) => state.user?.user?.id)
  const id = ownerId || (clientId ? clientId : userId)

  // Convert FileList to array if needed, filter out invalid files, and ensure all files have the CustomFile structure
  const filesArray: CustomFile[] = (Array.isArray(files) ? files : Array.from(files))
    .filter((file) => file != null && file.name && file.size != null)
    // Browsers give no type to kinds they do not know (.dwg, .log, no extension): those go up
    // as plain binary files instead of being left out
    .map((file) =>
      file.type
        ? file
        : new File([file], file.name, { type: UNKNOWN_FILE_TYPE, lastModified: file.lastModified })
    )
    .map((file) => {
      // If file already has invalid property, it's already a CustomFile
      if ('invalid' in file) {
        return file as CustomFile
      }
      // Otherwise, add the invalid property to make it a CustomFile
      // Use Object.assign to properly preserve File object properties
      return Object.assign(file, { invalid: false }) as CustomFile
    })

  if (filesArray.length === 0) {
    throw new Error('No valid files to upload')
  }

  const res = yield all(
    filesArray.map((file) => call(CommonApi.getSignedUrl, file.type, nameForLink(file.name)))
  )
  const signedUrl = res.map((response) => JSON.parse(response.getSignedUrl.data))

  const data = yield all(
    filesArray.map((file, index) =>
      call(CommonApi.uploadFile, signedUrl[index].signedUrl, file, file.type)
    )
  )
  return getFileData({ data, files: filesArray, signedUrl, tenantId, userId: id, isMultiple })
}

// The result says which upload it is (the caller's requestId), for callers that wait for theirs
function* uploadFile(action) {
  const requestId = action.payload?.requestId
  try {
    const { fileData } = yield uploadUserFile(
      action.payload?.files,
      action.payload?.isMultiple,
      action.payload?.userId
    )

    if (action?.payload?.saveFile) {
      yield call(CommonApi.saveUserFile, {
        sendNotification: false,
        files: action.payload?.isMultiple ? fileData : [fileData],
      })
    }
    yield put(
      uploadFileAction.FULLFILLED({
        fileData,
        requestId,
      })
    )
  } catch (error: any) {
    console.log('errors : ', error.message)
    yield put(uploadFileAction.FULLFILLED({ uploadFileErrors: error.message, requestId }))
  }
}
// Each result says whose list it is, so a list asked for someone else is never shown here
function* getUserFiles(action) {
  const userId = action.payload?.userId
  try {
    const tenantId = yield select((state) => state.user.tenantId)

    const res = yield call(CommonApi.getUserFiles, userId, tenantId)
    // The API reports its own failures in the response instead of throwing
    if (get(res, 'getUserFiles.success') === false) throw new Error('Could not load the files')
    const files = JSON.parse(get(res, 'getUserFiles.data', ''))

    yield put(getUserFilesAction.FULLFILLED({ userId, files }))
  } catch (error) {
    console.log('Could not load files:', error)
    yield put({ ...getUserFilesAction.REJECTED(), payload: { userId } })
  }
}

// The Files panel's uploads: they notify the owner of the files
const isFilesPanelUpload = (action) =>
  action.type === SAVE_USER_FILE.STARTED && !!action.payload?.generationNotification

// How long an upload waits for the list to show its files before it says it is done anyway
const LIST_WAIT_MS = 15000

/**
 * Uploads the files the Files panel picked and saves them to that panel's user. It runs to the
 * end even when the person leaves the page, and says how it went wherever they are.
 */
function* uploadToUserFiles(action) {
  const { userId, files } = action.payload
  let fileData
  try {
    fileData = (yield call(uploadUserFile, files, true, userId)).fileData
    const res = yield call(CommonApi.saveUserFile, { sendNotification: true, files: fileData })
    if (get(res, 'saveUserFile.success') === false) throw new Error('Could not save the files')
  } catch (error) {
    console.log('Could not upload files:', error)
    yield put({
      ...saveUserFileAction.REJECTED(),
      payload: { generationNotification: true, userId },
    })
    yield call(showErrorToast, i18n.t('files.uploadFailed'))
    return
  }

  // Saved. If this user's list is the one shown, it stays busy until the new files are in it.
  // When loading it fails the panel says so: the files are saved either way.
  const listUserId = yield select((state) => state.common.userFilesUserId)
  if (listUserId === userId) {
    yield put(getUserFilesAction.STARTED({ userId }))
    yield race([
      take(
        (result: any) =>
          [GET_USER_FILES.FULLFILLED, GET_USER_FILES.REJECTED].includes(result.type) &&
          result.payload?.userId === userId
      ),
      // Someone else's list was asked for (the person left): this one will not come
      take((next: any) => next.type === GET_USER_FILES.STARTED && next.payload?.userId !== userId),
      delay(LIST_WAIT_MS),
    ])
  }
  yield put(
    saveUserFileAction.FULLFILLED({
      generationNotification: true,
      userId,
      ids: fileData.map((file) => file.id),
    })
  )
  yield call(
    showSuccessToast,
    fileData.length === 1
      ? i18n.t('files.uploadedOne', { name: fileData[0].name || i18n.t('chat.preview.file') })
      : i18n.t('files.uploadedMany', { count: fileData.length })
  )
}

function* saveUserFile(action) {
  try {
    const tenantId = yield select((state) => state.user.tenantId)
    const payload = { ...action.payload, tenantId }
    yield call(CommonApi.saveUserFile, { files: payload, sendNotification: false })
    yield put(saveUserFileAction.FULLFILLED(action.payload))
  } catch (error) {
    console.log('Could not save file:', error)
    yield put(saveUserFileAction.REJECTED())
  }
}

/// /////////// Watchers ///////////////////////
export function* watcherCommon() {
  yield takeLatest(UPLOAD_FILE.STARTED, uploadFile)
  // Every upload from the Files panel runs on its own: a later one never cancels it
  yield takeEvery(isFilesPanelUpload, uploadToUserFiles)
  yield takeLatest(
    (action) => action.type === SAVE_USER_FILE.STARTED && !isFilesPanelUpload(action),
    saveUserFile
  )
  yield takeLatest(GET_USER_FILES.STARTED, getUserFiles)
}
